import { useIntl } from 'react-intl'
import styled, { css, keyframes } from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import SolidStarIcon from 'LuxKit/Icons/solid/SolidStarIcon'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import clsx from 'clsx'

export interface TestimonialReview {
  reviewerName: string
  text: string
  rating?: number
  date?: string
}

interface Props {
  reviews: Array<TestimonialReview>
  isStreaming?: boolean
}

const GOLD_LIGHT = '#E5C870'
const GOLD = '#C9A24B'
const GOLD_DARK = '#9C7A2E'

const Reviews = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

const ReviewBlock = styled.div`
  position: relative;
  padding-left: ${rem(36)};
`

const BigQuoteMark = styled.span`
  position: absolute;
  left: 0;
  top: ${rem(-4)};
  font-family: ${props => props.theme.font.secondary.family};
  font-size: ${rem(64)};
  line-height: 1;
  color: ${GOLD};
  opacity: 0.32;
  pointer-events: none;
  user-select: none;
`

const Bubble = styled.div`
  position: relative;
  background: #FFFFFF;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: ${rem(14)};
  padding: ${rem(16)} ${rem(18)};
  box-shadow:
    0 1px 1px rgba(15, 23, 42, 0.03),
    0 6px 20px -14px rgba(15, 23, 42, 0.18);
  display: flex;
  flex-direction: column;
  gap: ${rem(10)};
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(10)};
  min-width: 0;
`

const initialsAvatarBase = css`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(28)};
  height: ${rem(28)};
  border-radius: 50%;
  font-weight: 600;
  font-size: ${rem(11)};
  letter-spacing: 0.02em;
  color: #FFFFFF;
  background: linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 60%, ${GOLD_DARK} 100%);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.35);
`

const InitialsAvatar = styled.div`
  ${initialsAvatarBase};
`

const InitialsAvatarSkeleton = styled(LoadingBox)`
  flex-shrink: 0;
  width: ${rem(28)};
  height: ${rem(28)};
  border-radius: 50%;
`

const NameRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${rem(6)};
  min-width: 0;
  row-gap: ${rem(2)};
  flex: 1;
`

const ReviewerName = styled(BodyText)`
  font-weight: 700;
`

const MetaText = styled(BodyText)`
  color: ${props => props.theme.palette.neutral.default.three};
`

const ScoreInline = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  margin-left: auto;
  color: ${GOLD_DARK};
  font-weight: 600;
  font-size: ${rem(13)};
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;

  svg {
    color: ${GOLD};
  }
`

const QuoteText = styled.blockquote`
  margin: 0;
  font-family: ${props => props.theme.font.secondary.family};
  font-style: italic;
  font-size: ${rem(15)};
  line-height: 1.5;
  color: ${props => props.theme.palette.neutral.default.one};
`

const SCORE_MAX = 10

const testimonialStreamReveal = keyframes`
  from {
    opacity: 0;
    transform: translateY(${rem(8)});
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const StreamingReviewBlock = styled(ReviewBlock)`
  &.is-streaming {
    animation: ${testimonialStreamReveal} 180ms ease-out both;
  }

  @media (prefers-reduced-motion: reduce) {
    &.is-streaming {
      animation: none;
    }
  }
`

function formatScore(value: number): string {
  return value.toFixed(1)
}

function ScoreBadge({ rating }: { rating: number }) {
  const intl = useIntl()
  const clamped = Math.max(0, Math.min(SCORE_MAX, rating))
  const score = formatScore(clamped)

  const ariaLabel = intl.formatMessage(
    {
      id: 'ZW409ycC3Cx83XjI74MA',
      defaultMessage: 'Rating {score} out of {max}',
      description: 'Accessible label for the score badge on a chat testimonial review (e.g. Rating 8.5 out of 10)',
    },
    { score, max: SCORE_MAX },
  )

  return (
    <ScoreInline aria-label={ariaLabel}>
      <SolidStarIcon size="XXS" />
      {score}
    </ScoreInline>
  )
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '·'
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

// Ratings come straight off LE's review API, which is a 0-10 scale; the
// agent prompt mandates that scale. We trust the value and only clamp,
// rather than guessing from the magnitude.
function isDisplayableRating(rating: number | undefined): rating is number {
  return rating !== undefined && Number.isFinite(rating) && rating >= 0
}

function Testimonial({ reviews, isStreaming = false }: Props) {
  const intl = useIntl()
  const bullet = intl.formatMessage({
    id: '56MNhx0wfOosXYN8raap',
    defaultMessage: '·',
    description: 'Visual separator between a testimonial reviewer name and the review date',
  })
  const decorativeQuote = intl.formatMessage({
    id: 'GMJHMST7tT9Bg-AOKRS8',
    defaultMessage: '“',
    description: 'Decorative left double-quotation mark rendered alongside each chat testimonial review',
  })

  if (reviews.length === 0) {
    if (!isStreaming) {
      return null
    }

    return (
      <Reviews aria-busy="true" aria-live="polite" data-testid="testimonial-streaming-frame">
        <ReviewBlock>
          <BigQuoteMark aria-hidden="true">{decorativeQuote}</BigQuoteMark>
          <Bubble data-testid="testimonial-streaming-skeleton">
            <HeaderRow>
              <InitialsAvatarSkeleton floating />
              <NameRow>
                <TextLoadingBox typography="body-medium" width="36%" />
              </NameRow>
            </HeaderRow>
            <TextLoadingBox typography="body-medium" lines={2} width="100%" />
          </Bubble>
        </ReviewBlock>
      </Reviews>
    )
  }

  return (
    <Reviews>
      {reviews.map((review, idx) => (
        <StreamingReviewBlock
          key={`${review.reviewerName}-${idx}`}
          className={clsx(isStreaming && 'is-streaming')}
          data-testid={isStreaming ? 'testimonial-streaming-review' : undefined}
        >
          <BigQuoteMark aria-hidden="true">{decorativeQuote}</BigQuoteMark>
          <Bubble>
            <HeaderRow>
              <InitialsAvatar aria-hidden="true">
                {getInitials(review.reviewerName)}
              </InitialsAvatar>
              <NameRow>
                <ReviewerName variant="medium" colour="neutral-one">
                  {review.reviewerName}
                </ReviewerName>
                {review.date && (
                  <>
                    <MetaText variant="small">{bullet}</MetaText>
                    <MetaText variant="small">{review.date}</MetaText>
                  </>
                )}
              </NameRow>
              {isDisplayableRating(review.rating) && <ScoreBadge rating={review.rating} />}
            </HeaderRow>
            <QuoteText>{review.text}</QuoteText>
          </Bubble>
        </StreamingReviewBlock>
      ))}
    </Reviews>
  )
}

export default Testimonial
